import { useEffect } from "react";
import { FilterPoint, NB_POINTS_MAX } from "../../types/types";
import { useLocation, useNavigate } from "react-router-dom";

import { getTodayDate } from "../../utils/dateUtils";
import { getFilterFromURL, setFilterInURL } from "../../utils/filter.utils";
import { useDispatch, useSelector } from "react-redux";
import { filterMapState } from "../../redux/filter.mapstate";
import { setFilterAction } from "../../redux/filter.actions";
import { calculateDifferenceDate } from "../../../../common/utils/time.utils";

export default function useFilterCore() {
  const location: string = useLocation().search;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const filter = useSelector(filterMapState).filter.filter;

  const setFilterRaw = (filter: FilterPoint) => {
    dispatch(setFilterAction(filter));
  };

  const setFilter = (filter: FilterPoint) => {
    const reformatefilter = { ...filter };

    if (filter.startDate! && filter.stopDate!) {
      const startDateObj = new Date(filter.startDate);
      const stopDateObj = new Date(filter.stopDate);
      
      // Vérifier que les dates sont valides
      if (isNaN(startDateObj.getTime()) || isNaN(stopDateObj.getTime())) {
        console.warn('Invalid dates in useFilterCore:', { startDate: filter.startDate, stopDate: filter.stopDate });
        return;
      }
      
      const nbMinutes = calculateDifferenceDate(
        startDateObj,
        stopDateObj,
        "m"
      );
      const nbHours = calculateDifferenceDate(
        startDateObj,
        stopDateObj,
        "h"
      );
      const nbDays = calculateDifferenceDate(
        startDateObj,
        stopDateObj,
        "d"
      );
      const nbMonths = calculateDifferenceDate(
        startDateObj,
        stopDateObj,
        "mo"
      );
      const nb15MinIntervals = nbMinutes / 15;

      if (nb15MinIntervals > NB_POINTS_MAX) {
        if (filter.aggregateWindow === "15m") {
          reformatefilter.aggregateWindow = "1h";
          setFilterInURL(reformatefilter, location, navigate);
          setFilterRaw(reformatefilter);
          return;
        }
      }

      if (nbHours > NB_POINTS_MAX) {
        if (filter.aggregateWindow === "1h" || filter.aggregateWindow === "15m") {
          reformatefilter.aggregateWindow = "1d";
          setFilterInURL(reformatefilter, location, navigate);
          setFilterRaw(reformatefilter);
          return;
        }
      }

      if (nbDays > NB_POINTS_MAX) {
        if (
          filter.aggregateWindow === "1d" ||
          filter.aggregateWindow === "1h" ||
          filter.aggregateWindow === "15m"
        ) {
          reformatefilter.aggregateWindow = "1mo";
          setFilterInURL(reformatefilter, location, navigate);
          setFilterRaw(reformatefilter);

          return;
        }
      }

      if (nbDays <= 1) {
        if (
          filter.aggregateWindow === "1d" ||
          filter.aggregateWindow === "1mo"
        ) {
          reformatefilter.aggregateWindow = "1h";
          setFilterInURL(reformatefilter, location, navigate);
          setFilterRaw(reformatefilter);
          return;
        }
      } else if (nbMonths <= 1) {
        if (filter.aggregateWindow === "1mo") {
          reformatefilter.aggregateWindow = "1d";
          setFilterInURL(reformatefilter, location, navigate);
          setFilterRaw(reformatefilter);
        }
      }
    }

    setFilterInURL(reformatefilter, location, navigate);
    setFilterRaw(reformatefilter);
  };

  const handlePrevWindow = () => {
    const aggregateWindow = filter.aggregateWindow;
    const startDate = filter.startDate || new Date().toISOString();

    // Si c'est par heure (1h), on décrémente d'un jour complet
    if (aggregateWindow === "1h") {
      const startDateObj = new Date(startDate);
      // Aller au début du jour précédent
      startDateObj.setUTCDate(startDateObj.getUTCDate() - 1);
      startDateObj.setUTCHours(0, 0, 0, 0);
      
      // La fin est le début du jour suivant
      const stopDateObj = new Date(startDateObj);
      stopDateObj.setUTCDate(stopDateObj.getUTCDate() + 1);
      
      setFilter({
        ...filter,
        startDate: startDateObj.toISOString(),
        stopDate: stopDateObj.toISOString(),
        aggregateWindow,
      });
      return;
    }

    // Si c'est par 15 minutes (15m), on décrémente de 4 heures
    if (aggregateWindow === "15m") {
      const startDateObj = new Date(startDate);
      // Décrémenter de 4 heures
      startDateObj.setUTCHours(startDateObj.getUTCHours() - 4);
      // Arrondir les minutes à 0 pour commencer à l'heure pile
      startDateObj.setUTCMinutes(0, 0, 0);
      
      // La fin est 4 heures après le début
      const stopDateObj = new Date(startDateObj);
      stopDateObj.setUTCHours(stopDateObj.getUTCHours() + 4);
      
      setFilter({
        ...filter,
        startDate: startDateObj.toISOString(),
        stopDate: stopDateObj.toISOString(),
        aggregateWindow,
      });
      return;
    }

    // Si c'est jour (1d), on décrémente d'un mois complet
    if (aggregateWindow === "1d") {
      const startDateObj = new Date(startDate);
      // Aller au début du mois précédent
      startDateObj.setUTCMonth(startDateObj.getUTCMonth() - 1);
      startDateObj.setUTCDate(1);
      startDateObj.setUTCHours(0, 0, 0, 0);
      
      // La fin est le début du mois suivant
      const stopDateObj = new Date(startDateObj);
      stopDateObj.setUTCMonth(stopDateObj.getUTCMonth() + 1);
      
      setFilter({
        ...filter,
        startDate: startDateObj.toISOString(),
        stopDate: stopDateObj.toISOString(),
        aggregateWindow,
      });
      return;
    }

    // Si ce n'est pas mois (1mo), on décrémente d'une année complète
    if (aggregateWindow === "1mo") {
      const startDateObj = new Date(startDate);
      // Aller au début de l'année précédente (1er janvier)
      const prevYear = startDateObj.getUTCFullYear() - 1;
      const startDateNormalized = new Date(Date.UTC(prevYear, 0, 1, 0, 0, 0, 0));
      
      // La fin est le début de l'année suivante (1er janvier)
      const stopYear = prevYear + 1;
      const stopDateNormalized = new Date(Date.UTC(stopYear, 0, 1, 0, 0, 0, 0));
      
      setFilter({
        ...filter,
        startDate: startDateNormalized.toISOString(),
        stopDate: stopDateNormalized.toISOString(),
        aggregateWindow,
      });
      return;
    }
  };

  const handleNextWindow = () => {
    const aggregateWindow = filter?.aggregateWindow;
    const stopDate = filter?.stopDate || new Date().toISOString();

    // Si c'est par heure (1h), on incrémente d'un jour complet
    if (aggregateWindow === "1h") {
      const stopDateObj = new Date(stopDate);
      // Aller au début du jour suivant
      stopDateObj.setUTCDate(stopDateObj.getUTCDate() + 1);
      stopDateObj.setUTCHours(0, 0, 0, 0);
      
      // Le début est le début du jour précédent
      const startDateObj = new Date(stopDateObj);
      startDateObj.setUTCDate(startDateObj.getUTCDate() - 1);
      
      setFilter({
        ...filter,
        startDate: startDateObj.toISOString(),
        stopDate: stopDateObj.toISOString(),
        aggregateWindow,
      });
      return;
    }

    // Si c'est par 15 minutes (15m), on incrémente de 4 heures
    if (aggregateWindow === "15m") {
      const stopDateObj = new Date(stopDate);
      // Incrémenter de 4 heures
      stopDateObj.setUTCHours(stopDateObj.getUTCHours() + 4);
      // Arrondir les minutes à 0 pour commencer à l'heure pile
      stopDateObj.setUTCMinutes(0, 0, 0);
      
      // Le début est 4 heures avant la fin
      const startDateObj = new Date(stopDateObj);
      startDateObj.setUTCHours(startDateObj.getUTCHours() - 4);
      
      setFilter({
        ...filter,
        startDate: startDateObj.toISOString(),
        stopDate: stopDateObj.toISOString(),
        aggregateWindow,
      });
      return;
    }

    // Si c'est jour (1d), on incrémente d'un mois complet
    if (aggregateWindow === "1d") {
      const stopDateObj = new Date(stopDate);
      // Aller au début du mois suivant
      stopDateObj.setUTCMonth(stopDateObj.getUTCMonth() + 1);
      stopDateObj.setUTCDate(1);
      stopDateObj.setUTCHours(0, 0, 0, 0);
      
      // Le début est le début du mois précédent
      const startDateObj = new Date(stopDateObj);
      startDateObj.setUTCMonth(startDateObj.getUTCMonth() - 1);
      
      setFilter({
        ...filter,
        startDate: startDateObj.toISOString(),
        stopDate: stopDateObj.toISOString(),
        aggregateWindow,
      });
      return;
    }

    // Si ce n'est pas mois (1mo), on incrémente d'une année complète
    if (aggregateWindow === "1mo") {
      const stopDateObj = new Date(stopDate);
      // Aller au début de l'année suivante (1er janvier)
      const nextYear = stopDateObj.getUTCFullYear() + 1;
      const stopDateNormalized = new Date(Date.UTC(nextYear, 0, 1, 0, 0, 0, 0));
      
      // Le début est le début de l'année précédente (1er janvier)
      const startYear = nextYear - 1;
      const startDateNormalized = new Date(Date.UTC(startYear, 0, 1, 0, 0, 0, 0));
      
      setFilter({
        ...filter,
        startDate: startDateNormalized.toISOString(),
        stopDate: stopDateNormalized.toISOString(),
        aggregateWindow,
      });
      return;
    }
  };

  useEffect(() => {
    const filterURL = getFilterFromURL(location);
    
    // Vérifier si on vient du Grid Editor (paramètre from_plan présent)
    const urlParams = new URLSearchParams(location);
    const fromPlan = urlParams.get('from_plan');
    
    const initialeFilter: FilterPoint = {
      startDate: getTodayDate({ resetHour: true, nbDay: -30 }),
      stopDate: getTodayDate({ resetHour: true }),
      aggregateWindow: "1d",
    };

    // Si on vient du Grid Editor, toujours mettre à jour le filtre avec les données de l'URL
    if (fromPlan) {
      // Utiliser les données de l'URL ou les valeurs par défaut
      const finalFilter = {
        ...initialeFilter,
        ...filterURL, // Les données de l'URL (device_name, dates, aggregateWindow) remplacent les valeurs par défaut
      };
      
      // Si aucune fenêtre de temps n'est spécifiée dans l'URL, utiliser les valeurs par défaut
      if (!filterURL.startDate || !filterURL.stopDate || !filterURL.aggregateWindow) {
        finalFilter.startDate = initialeFilter.startDate;
        finalFilter.stopDate = initialeFilter.stopDate;
        finalFilter.aggregateWindow = initialeFilter.aggregateWindow;
      }
      
      setFilterRaw(finalFilter);
      return;
    }

    // Comportement normal pour les autres cas
    if (
      filterURL.startDate === undefined ||
      filterURL.stopDate === undefined ||
      filterURL.aggregateWindow === undefined
    ) {
      setFilter(initialeFilter);
      return;
    } else {
      setFilter(filterURL);
    }
    // Do not remove the comment below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
  return {
    setFilter,
    filter,
    handleNextWindow,
    handlePrevWindow,
  };
}

